import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, ExternalLink, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { ReliabilityBadge } from '../components/ReliabilityBadge';

export default function ArticleDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [summarizing, setSummarizing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/library/articles/${id}`);
        setArticle(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const summarize = async () => {
    setSummarizing(true);
    try {
      const { data } = await api.post('/ai/summarize', { article_id: id });
      setSummary(data.summary);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.common.error);
    } finally {
      setSummarizing(false);
    }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-24 text-muted-foreground">{t.common.loading}</div>;
  if (!article) return <div className="max-w-3xl mx-auto px-6 py-24">Not found.</div>;

  return (
    <article className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <Link to="/library" data-testid="back-to-library" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-10">
        <ArrowLeft size={16} /> {t.library.title}
      </Link>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <ReliabilityBadge level={article.reliability} size="lg" />
        <span className="text-xs uppercase tracking-widest text-muted-foreground">{article.category}</span>
        <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Clock size={12} /> {article.read_time_min} {t.library.min}</span>
      </div>
      <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight leading-[1.1] text-balance">
        {lang === 'it' ? article.title_it : article.title_en}
      </h1>
      <p className="mt-5 text-xl text-muted-foreground leading-relaxed">
        {lang === 'it' ? article.excerpt_it : article.excerpt_en}
      </p>
      <div className="mt-8 pb-8 border-b border-border flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
        <div>
          <span>{t.library.by} <span className="text-foreground font-medium">{article.author}</span></span>
          <span className="mx-2">·</span>
          <span>{t.library.updated}: {new Date(article.updated_at).toLocaleDateString(lang === 'it' ? 'it-IT' : 'en-GB')}</span>
        </div>
        <button onClick={summarize} disabled={summarizing} data-testid="article-summarize-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium hover:bg-secondary transition-colors disabled:opacity-50" style={{ borderColor: '#C5A059' }}>
          <Sparkles size={14} className="text-brand-gold-dark" />
          {summarizing ? t.common.loading : t.library.summarize}
        </button>
      </div>
      {summary && (
        <div className="mt-8 p-6 rounded-2xl border" style={{ borderColor: 'rgba(197,160,89,0.4)', backgroundColor: 'rgba(197,160,89,0.05)' }} data-testid="article-summary">
          <div className="text-xs uppercase tracking-widest text-brand-gold-dark font-medium mb-2 inline-flex items-center gap-1.5">
            <Sparkles size={12} /> {t.library.summary_title}
          </div>
          <p className="text-foreground/90 leading-relaxed whitespace-pre-line">{summary}</p>
        </div>
      )}
      <div className="prose prose-lg dark:prose-invert mt-10 text-foreground/90 leading-loose whitespace-pre-line">
        {lang === 'it' ? article.body_it : article.body_en}
      </div>
      {article.sources?.length > 0 && (
        <div className="mt-14 p-6 rounded-2xl bg-secondary border border-border">
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-3">{t.library.sources}</h3>
          <ul className="space-y-2">
            {article.sources.map((s, i) => (
              <li key={i} className="text-sm">
                {s.startsWith('http') ? (
                  <a href={s} target="_blank" rel="noreferrer" className="text-brand-deep dark:text-brand-gold-dark hover:underline inline-flex items-center gap-1.5">
                    {s} <ExternalLink size={12} />
                  </a>
                ) : (
                  <span className="text-foreground">{s}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {article.tags?.length > 0 && (
        <div className="mt-8 flex flex-wrap gap-2">
          {article.tags.map((tag) => (
            <span key={tag} className="text-xs px-3 py-1 rounded-full border border-border text-muted-foreground">#{tag}</span>
          ))}
        </div>
      )}
    </article>
  );
}
